import { Transition } from '@headlessui/react'
import yaml, { YAMLException } from 'js-yaml'
import { has } from 'lodash'
import { useEffect, useState } from 'react'
import { useMutation } from 'react-query'
import { JSONSchema3or4 } from 'to-json-schema'

import Button from '@/components/TailwindUI/Button'
import Monaco, { OnChange } from '@/pages/SpaceAppBuilder/DataSourcesBuilderPage/components/Monaco'
import proxyAction from '@/pages/SpaceAppBuilder/DataSourcesBuilderPage/globalActions/proxy.json'
import useDatasource from '@/pages/SpaceAppBuilder/DataSourcesBuilderPage/hooks/useDatasource'
import useSearchParams from '@/pages/SpaceAppBuilder/DataSourcesBuilderPage/hooks/useSearchParams'
import dump from '@/pages/SpaceAppBuilder/DataSourcesBuilderPage/utilities/dumpYaml'
import convertJSONtoSchema from '@/pages/SpaceAppBuilder/utilities/convertJSONToSchema'

export function Response() {
  const [datasource, setDatasource] = useDatasource(true)

  const datasourceActions: typeof datasource.actions = { _proxy: proxyAction, ...datasource.actions }

  const [searchParams] = useSearchParams()

  const activeActionKey = searchParams.get('action')!

  const [resultOverrideValue, setResultOverrideValue] = useState<null | object>(null)
  const [invalidJson, setInvalidJson] = useState(false)
  const [yamlSchema, setYamlSchema] = useState<JSONSchema3or4>()

  const updateResponseModelMutation = useMutation({
    mutationFn: async (variables: { actionKey: string; results: object }) => {
      const schema = convertJSONtoSchema(variables.results)
      setYamlSchema(schema)

      return setDatasource(datasource => ({
        ...datasource,
        actions: {
          ...datasource.actions,
          [variables.actionKey]: {
            ...datasource.actions[variables.actionKey],
            response: schema
          }
        }
      }))
    }
  })

  const handleResponseChange: OnChange = (value = '') => {
    try {
      if (!activeActionKey) return

      setDatasource(datasource => ({
        ...datasource,
        actions: {
          ...datasource.actions,
          [activeActionKey]: {
            ...(activeActionKey === '_proxy' ? proxyAction : {}),
            ...datasource.actions[activeActionKey],
            response: yaml.load(value)
          }
        }
      }))
    } catch (error) {
      if (error instanceof YAMLException) return
      throw error
    }
  }

  const handleGenerateResponseChange: OnChange = (value = '') => {
    if (!activeActionKey) return

    let parsedValue
    try {
      parsedValue = JSON.parse(value)
    } catch {
      setInvalidJson(true)
      return null
    }
    setInvalidJson(false)
    const hasResult = has(parsedValue, 'result')

    if (hasResult) {
      setResultOverrideValue(parsedValue)
    } else {
      if (Array.isArray(parsedValue)) {
        setResultOverrideValue({ result: [...parsedValue] })
      } else {
        setResultOverrideValue({ result: { ...parsedValue } })
      }
    }
  }

  useEffect(() => {
    if (updateResponseModelMutation.isSuccess) {
      const timeoutId = setTimeout(() => updateResponseModelMutation.reset(), 5000)
      return () => clearTimeout(timeoutId)
    }
  }, [updateResponseModelMutation.isSuccess, updateResponseModelMutation.reset])

  return (
    <>
      <div className="flex flex-1 flex-col">
        <div className="bg-gray-100 px-4 py-2 text-xs">Response</div>
        <Monaco
          key={activeActionKey}
          language="yaml"
          defaultValue={dump(datasourceActions[activeActionKey].response)}
          onChange={handleResponseChange}
          value={dump(yamlSchema)}
        />
      </div>
      <div className="flex flex-1 flex-col">
        <div className="bg-gray-100 px-4 py-2 text-xs">Generate Response Schema</div>
        <div className="relative flex-1">
          <div className="absolute right-20 top-2.5 z-10 flex flex-col items-center">
            <div className="flex">
              <Transition
                show={invalidJson}
                enter="transition-opacity duration-125"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="transition-opacity duration-300"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
                as="div"
                className="pointer-events-none pr-2 text-sm font-thin text-gray-400"
              >
                Invalid JSON
              </Transition>
              <Transition
                show={updateResponseModelMutation.isSuccess || updateResponseModelMutation.isError}
                enter="transition-opacity duration-125"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="transition-opacity duration-300"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
                as="div"
                className="pointer-events-none pr-2 text-sm font-thin text-gray-400"
              >
                {updateResponseModelMutation.isSuccess
                  ? 'Success!'
                  : updateResponseModelMutation.isError
                    ? 'Error updating schema'
                    : ''}
              </Transition>
              <Button
                color="secondary"
                disabled={invalidJson}
                onClick={() => {
                  if (activeActionKey && resultOverrideValue) {
                    updateResponseModelMutation.mutate({
                      actionKey: activeActionKey,
                      results: resultOverrideValue
                    })
                  }
                }}
              >
                Generate Response Schema
              </Button>
            </div>
          </div>
          <Monaco key={activeActionKey} language="json" onChange={handleGenerateResponseChange} />
        </div>
      </div>
    </>
  )
}
